import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LanguageContext";
import { ArrowDown } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const { t } = useLang();

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] w-full overflow-hidden bg-[#112A46]">
      <motion.div style={{ y }} className="absolute inset-0 h-[115%]">
        <img
          src="/images/villa-fachada.jpg"
          alt="Villa d'Avó Dalva"
          className="w-full h-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#112A46]/30 via-transparent to-[#112A46]/70" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
          className="text-[11px] uppercase tracking-[0.35em] text-white/80 mb-8"
          data-testid="hero-location"
        >
          {t.hero.location}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }}
          className="font-serif text-white text-[12vw] md:text-[8vw] lg:text-[7rem] leading-[0.9] font-light tracking-tighter max-w-5xl"
          data-testid="hero-title"
        >
          {t.hero.title_1}
          <span className="italic font-light text-[#F0EBE1]">{t.hero.title_2}</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-12 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#properties"
            data-testid="hero-cta-properties"
            className="inline-flex items-center justify-center bg-[#FAFAF7] text-[#112A46] px-10 py-5 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-[#C48E66] hover:text-white transition-all duration-300"
          >
            {t.hero.cta_primary}
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-book"
            className="inline-flex items-center justify-center border border-white/60 text-white px-10 py-5 text-[11px] uppercase tracking-[0.25em] font-medium hover:bg-white hover:text-[#112A46] transition-all duration-300"
          >
            {t.hero.cta_secondary}
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 right-6 md:right-12 z-10 flex flex-col items-center gap-3 text-white/70">
        <span className="text-[10px] uppercase tracking-[0.3em]" data-testid="hero-scroll">{t.hero.scroll}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1} />
        </motion.div>
      </div>
    </section>
  );
}
